import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import ModalWindow from '../ModalWindow/ModalWindow';
import ModalButton from '../ModalWindow/ModalButton/ModalButton';
import './WarStageUnitsConfirmModal.css';
import {WAR_COLOR} from '../../../styles/constants';
import {selectShowWarStageUnitsConfirmModal} from '../../../redux/reducers/privateStateReducer/selectors';
import {hideWarStageUnitsConfirmModal} from '../../../redux/actions';
import store from '../../../redux/store';
import {REGISTRY_SINGLETON} from '../../../scenes/Registry';
import {handleCompleteTurn} from '../../../utils/gameState/turn/handleCompleteTurn';
import {selectCurrentPlayer, selectPhase} from '../../../redux/reducers/sharedStateReducer/selectors';
import {PHASES} from '../../../constants/phases';
import {selectPhaseProgress} from '../../../utils/gameState/progress/selectPhaseProgress';
import {autoSelectNextUnit} from '../../../utils/unitAutoSelection/unitAutoSelectionCommands';
import {pluralizeRu} from '../../../utils/other/pluralizeRu';

const NO_IDS = [];

// Отряды игрока, который сейчас ходит, способные действовать и не пропустившие
// ход — те же, что ведет автовыбор и считает кнопка хода
const selectActionableUnitsCount = (state) => {
  const playerId = selectCurrentPlayer(state)?.id;
  return (playerId ? selectPhaseProgress(state, playerId).actionableIds : NO_IDS).length;
};

/**
 * Подтверждение завершения этапа войны (перемещение, способности, стрельба,
 * манёвр), пока у игрока есть отряды, способные действовать и не пропустившие
 * ход (spec unit-auto-selection, «Подтверждение завершения этапа»). Заменило
 * окна Лепреконов, Суккубов и Гоблинов: кража, соблазнение и способность —
 * частные случаи «отряд еще может действовать».
 * Смонтирована на уровне GameContent, как и прочие модалки: внутри кнопки хода
 * она попала бы в чужой stacking context и рисовалась под боковыми панелями.
 */
const WarStageUnitsConfirmModal = () => {
  const dispatch = useDispatch();
  const showModal = useSelector(selectShowWarStageUnitsConfirmModal);
  const unitsCount = useSelector(selectActionableUnitsCount);

  if (!showModal) {
    return null;
  }

  // «Вернуться»: окно закрывается, а автовыбор (если включен) выбирает
  // ближайший к центру кадра отряд, которому есть что делать
  const handleCancel = () => {
    dispatch(hideWarStageUnitsConfirmModal());
    autoSelectNextUnit(store, REGISTRY_SINGLETON.getGameScene()?.getFreeAreaCenterHexKey?.() || null);
  };

  const handleConfirm = () => {
    dispatch(hideWarStageUnitsConfirmModal());
    // Окно осталось открытым, а этап войны уже закрыл таймер хода — за ним
    // могла открыться следующая фаза (например, одновременная фаза снабжения):
    // подтверждение этапа войны не должно завершать ее без спроса
    if (selectPhase(store.getState()) !== PHASES.WAR) {
      return;
    }
    handleCompleteTurn(store, true, true, REGISTRY_SINGLETON.getSoundManager());
  };

  return (
    <ModalWindow handleClose={handleCancel} className={'war-stage-units-confirm-modal'}>
      <div className="war-stage-units-confirm-modal-content">
        <h2>{'Не все отряды сходили'}</h2>
        <p>
          {`Еще ${unitsCount} ${pluralizeRu(unitsCount, 'отряд может', 'отряда могут', 'отрядов могут')} действовать на этом этапе.`}
          <br/>
          {'Завершить этап?'}
        </p>
      </div>

      <div className="war-stage-units-confirm-modal-footer">
        <ModalButton
          onClick={handleConfirm}
          text={'Завершить этап'}
          className={'war-stage-units-confirm-modal-button'}
          style={{backgroundColor: WAR_COLOR}}
        />
        <ModalButton
          onClick={handleCancel}
          text={'Вернуться'}
          className={'war-stage-units-confirm-modal-button'}
        />
      </div>
    </ModalWindow>
  );
};

export default WarStageUnitsConfirmModal;
